import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Sparkles,
  Loader2,
  Eye,
  X,
  ChevronDown,
  ChevronRight,
  RotateCcw,
} from 'lucide-react';
import type { VideoTemplate } from '../templates';

export interface ColorAdjustments {
  brightness: number;   // -100 to 100 (0 = no change)
  contrast: number;     // -100 to 100 (0 = no change)
  saturation: number;   // -100 to 100 (0 = no change)
  temperature: number;  // -100 (cool) to 100 (warm), 0 = neutral
  exposure: number;     // -100 to 100 (0 = no change), maps to gamma
}

export const DEFAULT_COLOR_ADJUSTMENTS: ColorAdjustments = {
  brightness: 0,
  contrast: 0,
  saturation: 0,
  temperature: 0,
  exposure: 0,
};

export function hasColorAdjustments(adj: ColorAdjustments): boolean {
  return adj.brightness !== 0 || adj.contrast !== 0 || adj.saturation !== 0 || adj.temperature !== 0 || adj.exposure !== 0;
}

interface RenderControlsPanelProps {
  aspectRatio: string;
  setAspectRatio: (ratio: string) => void;
  renderMode: 'direct' | 'lambda';
  activeTemplate: VideoTemplate | null;
  setActiveTemplate: (template: VideoTemplate | null) => void;
  onShowTemplates: () => void;
  exportFilter: string;
  setExportFilter: (filter: string) => void;
  colorAdjustments: ColorAdjustments;
  setColorAdjustments: (adj: ColorAdjustments) => void;
  clipsCount: number;
  isLoadingPreview: boolean;
  onPreviewAI: () => void;
  onQuickPreview: () => void;
  onRender: () => void;
  compact?: boolean;
  [key: string]: any; // Accept extra props passed from parent
}

const SLIDER_CONFIG: { key: keyof ColorAdjustments; label: string; min: number; max: number }[] = [
  { key: 'brightness', label: 'Brightness', min: -100, max: 100 },
  { key: 'contrast', label: 'Contrast', min: -100, max: 100 },
  { key: 'saturation', label: 'Saturation', min: -100, max: 100 },
  { key: 'temperature', label: 'Temperature', min: -100, max: 100 },
  { key: 'exposure', label: 'Exposure', min: -100, max: 100 },
];

export const RenderControlsPanel: React.FC<RenderControlsPanelProps> = ({
  aspectRatio,
  setAspectRatio,
  renderMode,
  activeTemplate,
  setActiveTemplate,
  onShowTemplates,
  exportFilter,
  setExportFilter,
  colorAdjustments = DEFAULT_COLOR_ADJUSTMENTS,
  setColorAdjustments,
  clipsCount,
  isLoadingPreview,
  onPreviewAI,
  onQuickPreview,
  onRender,
  compact = false,
}) => {
  const maxWidthClass = compact ? '' : 'max-w-lg';
  const [showAdvanced, setShowAdvanced] = useState(false);
  const adjustmentsActive = hasColorAdjustments(colorAdjustments);

  const handleSliderChange = (key: keyof ColorAdjustments, value: number) => {
    setColorAdjustments({ ...colorAdjustments, [key]: value });
  };

  const handleReset = () => {
    setColorAdjustments({ ...DEFAULT_COLOR_ADJUSTMENTS });
  };

  return (
    <div className="space-y-4">
      {/* Aspect Ratio */}
      <div className="flex items-center gap-4">
        <Label className="text-xs text-muted-foreground">Output:</Label>
        <Select value={aspectRatio} onValueChange={setAspectRatio}>
          <SelectTrigger className="h-8 w-[180px] border-border bg-secondary/50 text-xs">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="border-border bg-popover">
            <SelectItem value="16:9" className="text-xs">16:9 (Landscape)</SelectItem>
            <SelectItem value="9:16" className="text-xs">9:16 (Portrait/TikTok)</SelectItem>
            <SelectItem value="1:1" className="text-xs">1:1 (Square)</SelectItem>
            <SelectItem value="4:5" className="text-xs">4:5 (Instagram)</SelectItem>
            <SelectItem value="21:9" className="text-xs">21:9 (Cinematic)</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Quick Render Settings - Combined Template + Filter */}
      {renderMode === 'direct' && (
        <div className={`w-full ${maxWidthClass} rounded-lg border border-border bg-[hsl(var(--panel))] p-3 space-y-3`}>
          {/* Template Row */}
          <div className="flex items-center justify-between gap-3">
            <Label className="text-xs text-muted-foreground whitespace-nowrap">Template:</Label>
            {activeTemplate ? (
              <div className="flex items-center gap-2 flex-1">
                <span className="text-xs font-medium">{activeTemplate.name}</span>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-5 px-1 text-xs text-muted-foreground hover:text-foreground"
                  onClick={() => setActiveTemplate(null)}
                >
                  <X className="h-3 w-3" />
                </Button>
              </div>
            ) : (
              <button className="text-xs text-primary hover:underline flex-1 text-left" onClick={onShowTemplates}>
                Choose a template
              </button>
            )}
          </div>

          {/* Export Filter / Color Grade Row */}
          <div className="flex items-center gap-3">
            <Label className="text-xs text-muted-foreground whitespace-nowrap">Grade:</Label>
            <Select
              value={exportFilter}
              onValueChange={(value: string) => setExportFilter(value)}
            >
              <SelectTrigger className="h-8 flex-1 border-border bg-secondary/50 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="border-border bg-popover">
                <SelectItem value="none" className="text-xs">None</SelectItem>
                <SelectItem value="cinematic-warm" className="text-xs">Cinematic Warm</SelectItem>
                <SelectItem value="cinematic-cool" className="text-xs">Cinematic Cool</SelectItem>
                <SelectItem value="teal-orange" className="text-xs">Teal & Orange</SelectItem>
                <SelectItem value="vintage" className="text-xs">Vintage</SelectItem>
                <SelectItem value="vivid" className="text-xs">Vivid</SelectItem>
                <SelectItem value="sunset" className="text-xs">Sunset</SelectItem>
                <SelectItem value="soft-glow" className="text-xs">Soft Glow</SelectItem>
                <SelectItem value="bleach-bypass" className="text-xs">Bleach Bypass</SelectItem>
                <SelectItem value="film-noir" className="text-xs">Film Noir</SelectItem>
                <SelectItem value="high-contrast-bw" className="text-xs">High Contrast B&W</SelectItem>
                <SelectItem value="boost" className="text-xs">Boost</SelectItem>
                <SelectItem value="contrast" className="text-xs">Contrast</SelectItem>
                <SelectItem value="muted" className="text-xs">Muted</SelectItem>
                <SelectItem value="darken" className="text-xs">Darken</SelectItem>
                <SelectItem value="lighten" className="text-xs">Lighten</SelectItem>
                <SelectItem value="greyscale" className="text-xs">Greyscale</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Advanced Color Grading Toggle */}
          <button
            className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
            onClick={() => setShowAdvanced(!showAdvanced)}
          >
            {showAdvanced ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
            <span>Advanced Adjustments</span>
            {adjustmentsActive && !showAdvanced && (
              <span className="ml-1 h-1.5 w-1.5 rounded-full bg-primary inline-block" />
            )}
          </button>

          {/* Advanced Color Grading Sliders */}
          {showAdvanced && (
            <div className="space-y-3 pt-1">
              {SLIDER_CONFIG.map(({ key, label, min, max }) => (
                <div key={key} className="space-y-1">
                  <div className="flex items-center justify-between">
                    <Label className="text-xs text-muted-foreground">{label}</Label>
                    <span className="text-xs tabular-nums text-muted-foreground w-8 text-right">
                      {colorAdjustments[key] > 0 ? '+' : ''}{colorAdjustments[key]}
                    </span>
                  </div>
                  <Slider
                    value={[colorAdjustments[key]]}
                    onValueChange={([v]) => handleSliderChange(key, v)}
                    min={min}
                    max={max}
                    step={1}
                    className="w-full"
                  />
                </div>
              ))}

              {/* Reset button */}
              {adjustmentsActive && (
                <button
                  className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors pt-1"
                  onClick={handleReset}
                >
                  <RotateCcw className="h-3 w-3" />
                  Reset adjustments
                </button>
              )}
            </div>
          )}
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex gap-3">
        {renderMode === 'direct' && (
          <Button
            variant="outline"
            disabled={clipsCount === 0}
            onClick={onQuickPreview}
            size="lg"
            className="border-border"
          >
            <Eye className="mr-2 h-4 w-4" />
            Preview Timeline
          </Button>
        )}

        {renderMode === 'lambda' && (
          <Button
            variant="outline"
            disabled={clipsCount === 0 || isLoadingPreview}
            onClick={onPreviewAI}
            size="lg"
            className="border-border"
          >
            {isLoadingPreview ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Sparkles className="mr-2 h-4 w-4" />
            )}
            {isLoadingPreview ? 'Generating...' : 'Preview AI Edit'}
          </Button>
        )}

        <Button
          data-tour="render-button"
          disabled={clipsCount === 0}
          onClick={onRender}
          size="lg"
          className="bg-primary text-primary-foreground hover:bg-primary/90"
        >
          <Sparkles className="mr-2 h-4 w-4" />
          Render
        </Button>
      </div>
    </div>
  );
};
